import { useEffect, useRef, useState } from 'react';
import {
  XMarkIcon, ArrowTopRightOnSquareIcon, ArrowDownTrayIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
// pdf.js's own text-layer styles (hand-copied — see DocumentViewer.css):
// transparent, absolutely-positioned spans laid exactly over the canvas
// glyphs they mirror, which is what makes the drawing below
// selectable/copyable rather than just a picture of text.
import './DocumentViewer.css';

// Full-screen reader for a message's Document block, so a quick guide can be
// read without leaving the app. Shared by the dashboard and the FSM app (same
// file in both) -- keep them identical.
//
// PDFs are drawn with pdf.js rather than handed to the browser: phone browsers
// and the app's web view do not display a PDF inline, they download it, which
// is the "sends me somewhere else" this replaces.
//  - uploaded PDF       -> its own capability URL
//  - library document   -> a short-lived link from the API; the server fetches
//                          the current version and converts Office files to PDF
//  - api-fetch           -> an authenticated JSON-API endpoint returning a PDF
//                          (AbsenceReportingList.jsx's report attachments) --
//                          fetched as bytes via `api` (so the normal bearer
//                          token applies) rather than a bare URL, since pdf.js
//                          has no way to attach an Authorization header to the
//                          request a plain { url } config makes itself
//  - anything else      -> download / open in SharePoint, from this screen

const isPdf = (b) => /\.pdf$/i.test(b.name || '') || b.mime === 'application/pdf';

async function resolveSource(block, commId, mediaBase) {
  if (block.source === 'upload' && block.mediaId) {
    const url = `${mediaBase}/api/comms/media/${block.mediaId}?k=${encodeURIComponent(block.token || '')}`;
    return isPdf(block) ? { pdfUrl: url, downloadUrl: url } : { downloadUrl: url };
  }
  if (block.source === 'library' && block.itemId) {
    // In a sent message the API checks the reader received it; in the composer's
    // preview (no message yet) the author is Communications staff.
    const { data } = commId
      ? await api.post(`/api/comms/${commId}/documents/${encodeURIComponent(block.id)}/view`, {})
      : await api.post('/api/comms/library/view', { itemId: block.itemId });
    return { pdfUrl: `${mediaBase}${data.url}`, externalUrl: block.url };
  }
  if (block.source === 'api-fetch' && block.url) {
    const { data } = await api.get(block.url, { responseType: 'arraybuffer' });
    // A blob: URL works for the Download link exactly like a real one would
    // (no CORS/auth to worry about, it's already local bytes) — no need for
    // a second, differently-named field just for that.
    const downloadUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    return { pdfBytes: new Uint8Array(data), downloadUrl, revokeDownloadUrl: true };
  }
  return { externalUrl: block.url };
}

// One page, drawn only while it is on or near the screen and cleared again when
// scrolled well away -- a 40-page procedure drawn all at once can exhaust a
// phone's memory.
function PdfPage({ doc, pageNumber, width, scroller, pdfjs }) {
  const holderRef = useRef(null);
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const [size, setSize] = useState(null);
  const [near, setNear] = useState(pageNumber <= 2);

  useEffect(() => {
    const el = holderRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setNear(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setNear(e.isIntersecting), { root: scroller, rootMargin: '1200px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [scroller]);

  useEffect(() => {
    if (!width) return undefined;
    let cancelled = false;
    let task = null;
    let textLayerTask = null;
    (async () => {
      const page = await doc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
      if (cancelled) return;
      setSize({ w: viewport.width, h: viewport.height });
      const canvas = canvasRef.current;
      if (!near || !canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      task = page.render({ canvasContext: canvas.getContext('2d'), viewport, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null });
      await task.promise;
      if (cancelled) return;

      // An invisible layer of real, selectable text positioned exactly over
      // the glyphs just drawn to canvas — the canvas itself is only pixels,
      // nothing a "select text"/copy gesture can reach without this.
      const textLayerDiv = textLayerRef.current;
      if (textLayerDiv && pdfjs) {
        textLayerDiv.replaceChildren();
        textLayerDiv.style.setProperty('--scale-factor', viewport.scale);
        const textContent = await page.getTextContent();
        if (cancelled) return;
        textLayerTask = pdfjs.renderTextLayer({ textContentSource: textContent, container: textLayerDiv, viewport });
        await textLayerTask.promise;
      }
    })().catch((e) => { if (e?.name !== 'RenderingCancelledException') console.error('pdf page', pageNumber, e); });
    return () => { cancelled = true; if (task) task.cancel(); if (textLayerTask) textLayerTask.cancel(); };
  }, [doc, pageNumber, width, near, pdfjs]);

  const w = size?.w || width;
  const h = size?.h || width * 1.414;
  return (
    <div ref={holderRef} className="relative mx-auto bg-white shadow" style={{ width: w, height: h }}>
      {near && <canvas ref={canvasRef} />}
      {near && <div ref={textLayerRef} className="textLayer" />}
    </div>
  );
}

export default function DocumentViewer({ block, commId = null, mediaBase = '', onClose }) {
  const [source, setSource] = useState(null);
  const [doc, setDoc] = useState(null);
  const [pdfjsLib, setPdfjsLib] = useState(null);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);
  const [boxWidth, setBoxWidth] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    let live = true;
    resolveSource(block, commId, mediaBase)
      .then((s) => { if (live) setSource(s); else if (s.revokeDownloadUrl) URL.revokeObjectURL(s.downloadUrl); })
      .catch((e) => { if (live) { setSource({ externalUrl: block.url }); setError(e.response?.data?.error || 'The document could not be opened.'); } });
    return () => { live = false; };
  }, [block, commId, mediaBase]);

  // The api-fetch source's downloadUrl is a blob: URL made just for this
  // component instance (see resolveSource) — nothing else holds a reference
  // to revoke it, unlike the comms media/library URLs, which are plain http
  // URLs needing no cleanup at all.
  useEffect(() => {
    if (!source?.revokeDownloadUrl) return undefined;
    return () => URL.revokeObjectURL(source.downloadUrl);
  }, [source]);

  // pdf.js is loaded only when somebody opens a document, as its own chunk,
  // and runs on the page itself (no separate worker file to serve or cache).
  useEffect(() => {
    if (!source?.pdfUrl && !source?.pdfBytes) return undefined;
    let cancelled = false;
    let loadingTask = null;
    (async () => {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
      await import('pdfjs-dist/legacy/build/pdf.worker.entry');
      loadingTask = pdfjs.getDocument(source.pdfBytes ? { data: source.pdfBytes } : { url: source.pdfUrl });
      const d = await loadingTask.promise;
      if (cancelled) { d.destroy(); return; }
      setPdfjsLib(pdfjs);
      setDoc(d);
    })().catch((e) => {
      if (!cancelled) setError(/expired|403/i.test(String(e?.message)) ? 'This link has expired — close the document and open it again.' : 'The document could not be shown here.');
    });
    return () => { cancelled = true; if (loadingTask) loadingTask.destroy(); };
  }, [source]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setBoxWidth(el.clientWidth);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const pageWidth = Math.max(0, Math.min(boxWidth - 16, 900)) * zoom;
  const title = block.title || block.name || 'Document';
  const iconBtn = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/10 disabled:opacity-40';

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-gray-800" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex items-center gap-1 bg-gray-900 px-2 py-1.5 text-white">
        <button type="button" onClick={onClose} className={iconBtn} aria-label="Close document"><XMarkIcon className="h-6 w-6" /></button>
        <div className="min-w-0 flex-1 px-1">
          <p className="truncate text-sm font-semibold">{title}</p>
          {doc && <p className="text-[11px] text-gray-400">{doc.numPages} page{doc.numPages === 1 ? '' : 's'}</p>}
        </div>
        {doc && (
          <>
            <button type="button" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))} disabled={zoom <= 0.5} className={iconBtn} aria-label="Zoom out"><MagnifyingGlassMinusIcon className="h-6 w-6" /></button>
            <button type="button" onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))} disabled={zoom >= 3} className={iconBtn} aria-label="Zoom in"><MagnifyingGlassPlusIcon className="h-6 w-6" /></button>
          </>
        )}
        {source?.downloadUrl && (
          <a href={source.downloadUrl} target="_blank" rel="noreferrer" className={iconBtn} aria-label="Download"><ArrowDownTrayIcon className="h-6 w-6" /></a>
        )}
        {source?.externalUrl && /^https:\/\//i.test(source.externalUrl) && (
          <a href={source.externalUrl} target="_blank" rel="noreferrer" className={iconBtn} aria-label="Open in SharePoint"><ArrowTopRightOnSquareIcon className="h-6 w-6" /></a>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto py-3">
        {!source && !error && <p className="mt-16 text-center text-sm text-gray-300">Opening…</p>}
        {(source?.pdfUrl || source?.pdfBytes) && !doc && !error && <p className="mt-16 text-center text-sm text-gray-300">Loading document…</p>}
        {doc && pageWidth > 0 && (
          <div className="space-y-3 px-2" style={{ minWidth: pageWidth + 16 }}>
            {Array.from({ length: doc.numPages }, (_, i) => (
              <PdfPage key={`${i}-${zoom}`} doc={doc} pageNumber={i + 1} width={pageWidth} scroller={scrollRef.current} pdfjs={pdfjsLib} />
            ))}
          </div>
        )}
        {(error || (source && !source.pdfUrl && !source.pdfBytes)) && (
          <div className="mx-auto mt-16 max-w-sm px-6 text-center text-gray-200">
            <p className="text-sm">{error || "This type of file can't be shown inside the app."}</p>
            <div className="mt-4 flex flex-col gap-2">
              {source?.downloadUrl && (
                <a href={source.downloadUrl} target="_blank" rel="noreferrer" className="rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white">Download it</a>
              )}
              {source?.externalUrl && /^https:\/\//i.test(source.externalUrl) && (
                <a href={source.externalUrl} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-500 px-4 py-3 text-sm font-semibold text-white">Open it in SharePoint</a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
